// combatpro/views/PlayerView.js
//
// Combat PRO - PLAYER VIEW (read-only presentation mode).
//
// An optional clean, read-only display for a second monitor / tablet / TV at the
// table. It is NOT a remote VTT or player client and exposes NO editing controls.
// It reads the same authenticated, entitled Combat PRO state (the active encounter,
// round, combatants, conditions) and presents only what the DM chooses to expose.
//
// Privacy by default: exact monster HP and AC, private notes, DM-only hazards, hidden
// enemies, secret waves/phases, Treasure and tactics are NEVER shown. A compact DM
// control strip at the very top (clearly the DM's, not part of the clean display)
// toggles the few optional public fields live. Everything below the DM strip is the
// player-facing surface. No optional chaining, no nullish coalescing, no em dash.

import React, { useContext, useState } from 'react';
import { CombatContext } from '../contexts/CombatContext';
import { buildPlayerSafeCombatProjection } from '../integration/playerVisibilityEngine';

function Row(props) {
  return (
    <div className={'vi-pv-row' + (props.current ? ' is-current' : '') + (props.side ? (' side-' + props.side) : '')}>
      <div className="vi-pv-row-main">
        {props.current ? <span className="vi-pv-turn">Now</span> : null}
        <span className="vi-pv-name">{props.name}</span>
      </div>
      <div className="vi-pv-row-side">
        {props.status ? <span className={'vi-pv-status st-' + String(props.status).toLowerCase()}>{props.status}</span> : null}
        {props.conditions && props.conditions.length ? (
          <span className="vi-pv-conds">{props.conditions.join(', ')}</span>
        ) : null}
      </div>
    </div>
  );
}

function PlayerView() {
  const { state } = useContext(CombatContext);
  const [showEnemyNames, setShowEnemyNames] = useState(true);
  const [showEnemyHP, setShowEnemyHP] = useState(false);
  const [showConditions, setShowConditions] = useState(true);
  const [showPlayerHP, setShowPlayerHP] = useState(true);
  const [showObjective, setShowObjective] = useState(false);

  const projection = buildPlayerSafeCombatProjection(state, { showEnemyNames, showEnemyHP, showConditions, showPlayerHP, showObjective });

  return (
    <div className="vi-view vi-pv-view" id="view-player-view">
      <div className="vi-pv-dmbar">
        <span className="vi-pv-dmbar-label">DM controls (not shown to players)</span>
        <label className="vi-pv-toggle"><input type="checkbox" checked={showEnemyNames} onChange={function () { setShowEnemyNames(!showEnemyNames); }} /> Enemy names</label>
        <label className="vi-pv-toggle"><input type="checkbox" checked={showEnemyHP} onChange={function () { setShowEnemyHP(!showEnemyHP); }} /> Enemy HP</label>
        <label className="vi-pv-toggle"><input type="checkbox" checked={showConditions} onChange={function () { setShowConditions(!showConditions); }} /> Conditions</label>
        <label className="vi-pv-toggle"><input type="checkbox" checked={showPlayerHP} onChange={function () { setShowPlayerHP(!showPlayerHP); }} /> Player HP</label>
        <label className="vi-pv-toggle"><input type="checkbox" checked={showObjective} onChange={function () { setShowObjective(!showObjective); }} /> Objective</label>
      </div>

      <div className="vi-pv-stage">
        <div className="vi-pv-head">
          <div className="vi-pv-enc">{projection.encounter}</div>
          <div className="vi-pv-round"><span className="vi-pv-round-k">Round</span> <span className="vi-pv-round-v">{projection.round}</span></div>
        </div>

        {projection.objective ? (
          <div className="vi-pv-objective"><span className="vi-pv-obj-k">Objective</span> {projection.objective}</div>
        ) : null}

        <div className="vi-pv-cols">
          <div className="vi-pv-col">
            <div className="vi-pv-col-title">Party</div>
            {projection.party.length ? projection.party.map(function (row) {
              return <Row key={row.key} side="player" name={row.name} status={row.status} conditions={row.conditions} current={false} />;
            }) : <div className="vi-pv-empty">No players added.</div>}
          </div>

          <div className="vi-pv-col">
            <div className="vi-pv-col-title">Enemies</div>
            {projection.enemies.length ? projection.enemies.map(function (row) {
              return <Row key={row.key} side="enemy" name={row.name} status={row.status} conditions={row.conditions} current={row.current} />;
            }) : <div className="vi-pv-empty">No enemies in this encounter.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PlayerView;
